import {
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useState } from 'react';
import type { ReactNode } from 'react';
import useRetrieveStepRunConnections from '@/hooks/api/stepExecutions/useRetrieveStepRunConnections.ts';
import useRetrieveStepRunLogPresentation from '@/hooks/api/stepExecutions/useRetrieveStepRunLogPresentation.ts';
import LoadedInputDetails from '@/pages/Run/components/StepExecutionLogs/LoadedInputDetails.tsx';
import DatasetPill from '@/pages/Run/components/DatasetPill.tsx';
import { AutomationStep } from '@/types/automationSteps.ts';
import { StepRun, StepRunConnection, StepRunReference } from '@/types/stepExecutions.ts';

type Props = {
  stepRun?: StepRun;
  stepsById: Record<string, AutomationStep>;
  enabled?: boolean;
  withDivider?: boolean;
};

const StepRunConnectionsPanel = ({
  stepRun,
  stepsById,
  enabled = true,
  withDivider = true,
}: Props) => {
  const { data, isPending, isError } = useRetrieveStepRunConnections(stepRun?.id, enabled);
  const {
    data: presentation,
    isPending: isBindingsPending,
    isError: isBindingsError,
  } = useRetrieveStepRunLogPresentation(stepRun?.id, enabled && !!stepRun);

  if (!stepRun || !enabled) return null;

  const inputConnections = data?.input_connections ?? data?.consumed ?? [];
  const outputBindings = data?.output_bindings ?? data?.produced ?? [];
  const downstreamConsumers = data?.downstream_consumers ?? data?.consumed_by ?? [];
  const hasConnections =
    inputConnections.length > 0 || outputBindings.length > 0 || downstreamConsumers.length > 0;

  return (
    <>
      {withDivider && <Divider />}
      <Box px="md" py="xs">
        <details style={{ overflowWrap: 'anywhere' }} key={stepRun.id}>
          <summary>
            <Text component="span" size="xs" c="dimmed">
              Input binding details
            </Text>
          </summary>
          <Stack gap="xs" my="xs">
            <Text size="xs" c="dimmed">
              Loaded references and configured purposes do not prove that a branch executed.
            </Text>
            {isBindingsPending && <Text size="xs">Loading input binding details...</Text>}
            {isBindingsError && (
              <Text size="xs" c="red">
                Unable to {presentation ? 'refresh' : 'load'} input binding details.
              </Text>
            )}
            {presentation?.inputs.length === 0 && (
              <Text size="xs" c="dimmed">
                No public input bindings recorded.
              </Text>
            )}
            {presentation?.inputs.map((input, index) => (
              <Stack key={`${input.dataset.id}-${index}`} gap={3}>
                <DatasetPill
                  datasetId={input.dataset.id}
                  datasetName={input.dataset.name}
                  dataset={input.dataset}
                />
                <LoadedInputDetails input={input} />
              </Stack>
            ))}
          </Stack>
        </details>
        {isPending && (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="xs" c="dimmed">
              Loading references
            </Text>
          </Group>
        )}

        {isError && (
          <Text size="xs" c="red">
            Unable to load references.
          </Text>
        )}

        {data && !hasConnections && (
          <Text size="xs" c="dimmed">
            No connections recorded.
          </Text>
        )}

        {data && hasConnections && (
          <Stack gap="xs">
            <ConnectionSection
              title="Loaded input references"
              emptyLabel="No inputs"
              connections={inputConnections}
              renderConnection={(connection) => (
                <ConsumedConnectionRow connection={connection} stepsById={stepsById} />
              )}
            />
            <ConnectionSection
              title="Outputs"
              emptyLabel="No outputs"
              connections={outputBindings}
              renderConnection={(connection) => <ProducedConnectionRow connection={connection} />}
            />
            <ConnectionSection
              title="Used by later steps"
              emptyLabel="No downstream steps"
              connections={downstreamConsumers}
              renderConnection={(connection) => (
                <ConsumedByConnectionRow connection={connection} stepsById={stepsById} />
              )}
            />
          </Stack>
        )}
      </Box>
    </>
  );
};

type ConnectionSectionProps = {
  title: string;
  emptyLabel: string;
  connections: StepRunConnection[];
  renderConnection: (connection: StepRunConnection) => ReactNode;
};

const MAX_VISIBLE_CONNECTIONS = 5;

const ConnectionSection = ({
  title,
  emptyLabel,
  connections,
  renderConnection,
}: ConnectionSectionProps) => {
  const [showAll, setShowAll] = useState(false);
  const visibleConnections = showAll ? connections : connections.slice(0, MAX_VISIBLE_CONNECTIONS);
  const hiddenConnectionCount = connections.length - visibleConnections.length;

  return (
    <Stack gap={6}>
      <Group gap={6} justify="space-between" wrap="nowrap">
        <Text size="11px" fw={600} c="dimmed" tt="uppercase">
          {title}
        </Text>
        <Badge size="xs" color={connections.length > 0 ? 'blue' : 'gray'} variant="light">
          {connections.length}
        </Badge>
      </Group>
      {connections.length > 0 ? (
        <Stack gap={6}>
          {visibleConnections.map((connection, index) => (
            <Box key={connectionKey(connection, index)}>{renderConnection(connection)}</Box>
          ))}
          {hiddenConnectionCount > 0 && (
            <Button
              variant="subtle"
              color="gray"
              size="compact-xs"
              onClick={() => setShowAll(true)}
              style={{ alignSelf: 'flex-start' }}
            >
              Show {hiddenConnectionCount} more
            </Button>
          )}
        </Stack>
      ) : (
        <Text size="11px" c="dimmed">
          {emptyLabel}
        </Text>
      )}
    </Stack>
  );
};

type RunConnectionRowShellProps = {
  title: ReactNode;
  details?: ReactNode;
};

const RunConnectionRowShell = ({ title, details }: RunConnectionRowShellProps) => (
  <Box
    style={{
      background: '#FDFDFD',
      border: '1px solid #ECECED',
      borderRadius: 6,
      padding: '8px 10px',
    }}
  >
    <Group gap={6} wrap="wrap" align="center">
      {title}
    </Group>
    {details && (
      <Group gap={4} wrap="wrap" mt={4} align="center">
        {details}
      </Group>
    )}
  </Box>
);

type RunConnectionRowProps = {
  connection: StepRunConnection;
  stepsById: Record<string, AutomationStep>;
};

const DetailText = ({ children }: { children: ReactNode }) => (
  <Text size="11px" c="dimmed" component="span">
    {children}
  </Text>
);

const ConsumedConnectionRow = ({ connection, stepsById }: RunConnectionRowProps) => {
  const providerStepRun = connection.provider_step_run ?? connection.producer_step_run;
  const providerOutputId = connection.provider_output_id ?? connection.producer_output_id;
  const providerIsOrigin =
    providerStepRun?.id === connection.origin_step_run?.id &&
    providerOutputId === connection.origin_output_id;
  const providerLabel =
    connection.provider_binding_kind === 'source_input'
      ? 'Source provided by'
      : providerIsOrigin && connection.origin_binding_kind === 'produced'
        ? 'Created by'
        : 'Provided by';
  const originLabel =
    connection.origin_binding_kind === 'produced'
      ? 'Created by'
      : connection.origin_binding_kind === 'source_input'
        ? 'Source provided by'
        : 'Origin';

  return (
    <RunConnectionRowShell
      title={
        <DatasetPill
          datasetId={connection.dataset.id}
          datasetName={connection.dataset.name}
          dataset={connection.dataset}
        />
      }
      details={
        <>
          {providerStepRun ? (
            <>
              <DetailText>{providerLabel}</DetailText>
              <StepRunLink stepRun={providerStepRun} stepsById={stepsById} />
            </>
          ) : (
            <DetailText>Provided directly</DetailText>
          )}
          {connection.origin_step_run && !providerIsOrigin && (
            <>
              <DetailText>{originLabel}</DetailText>
              <StepRunLink stepRun={connection.origin_step_run} stepsById={stepsById} />
            </>
          )}
        </>
      }
    />
  );
};

const ProducedConnectionRow = ({ connection }: { connection: StepRunConnection }) => (
  <RunConnectionRowShell
    title={
      <DatasetPill
        datasetId={connection.dataset.id}
        datasetName={connection.dataset.name}
        dataset={connection.dataset}
      />
    }
  />
);

const ConsumedByConnectionRow = ({ connection, stepsById }: RunConnectionRowProps) => (
  <RunConnectionRowShell
    title={
      connection.consumer_step_run ? (
        <StepRunLink stepRun={connection.consumer_step_run} stepsById={stepsById} />
      ) : (
        <Text size="12px" fw={500}>
          Later step
        </Text>
      )
    }
    details={
      <>
        <DetailText>Uses</DetailText>
        <DatasetPill
          datasetId={connection.dataset.id}
          datasetName={connection.dataset.name}
          dataset={connection.dataset}
        />
      </>
    }
  />
);

type StepRunLinkProps = {
  stepRun: StepRunReference;
  stepsById: Record<string, AutomationStep>;
};

const StepRunLink = ({ stepRun, stepsById }: StepRunLinkProps) => {
  const stepName = stepsById[stepRun.step_id]?.name || `Step ${stepRun.step_index + 1}`;
  const attemptLabel = `Attempt ${stepRun.attempt_number + 1}`;

  return (
    <UnstyledButton
      onClick={() => {
        document.getElementById(`step-run-${stepRun.id}`)?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }}
      style={{ maxWidth: '220px' }}
    >
      <Group gap={6} wrap="nowrap">
        <Text size="12px" c="blue" fw={500} truncate title={stepName}>
          {stepName}
        </Text>
        <Badge size="xs" variant="light" color="violet" style={{ flexShrink: 0 }}>
          {attemptLabel}
        </Badge>
      </Group>
    </UnstyledButton>
  );
};

const connectionKey = (connection: StepRunConnection, index: number): string =>
  [
    connection.dataset.id,
    connection.output_id,
    connection.producer_output_id,
    connection.producer_step_run?.id,
    connection.consumer_step_run?.id,
    index,
  ]
    .filter(Boolean)
    .join(':');

export default StepRunConnectionsPanel;
